import LinkButton, {
  Button as LinkButtonContainer,
} from "@components/buttons/LinkButton"
import { Add, Check, Close } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body14Styles,
  body16BoldStyles,
  body16MainStyles,
  smallcapsBold13Styles,
  smallcapsBold14Styles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import {
  DesktopOnly,
  DesktopOnlyContainer,
  MobileAndTabletOnly,
} from "@utils/MediaQuery"
import { formatCurrency } from "@utils/numbers"
import React from "react"
import styled, { css } from "styled-components"

import { DiscountResponseProps } from "./types"

const ESCAPE_KEY = "Escape"
const ENTER_KEY = "Enter"

const Container = styled.div`
  position: relative;
  padding: 20px 20px 58px;

  @media ${breakpoints.tablet} {
    padding: 20px 32px 58px;
  }

  @media ${breakpoints.laptop} {
    flex-shrink: 0;
    width: 444px;
    height: 100%;
    padding: 56px 64px 0;
    border-left: solid 1px ${colors.dividerLineGray};
  }

  ${LinkButtonContainer} {
    justify-content: center;
  }

  ${LinkButtonContainer}, span {
    padding: 0;
  }

  ${DesktopOnlyContainer} {
    position: relative;
  }
`

const Row = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  margin-bottom: 6px;
`

const Header = styled.div`
  ${smallcapsBold14Styles}
`

const Text = styled.div<{ $noWrap?: boolean }>`
  ${({ $noWrap }) => css`
    ${body16MainStyles}
    white-space: ${$noWrap ? "nowrap" : "initial"};
    color: ${colors.mediumDarkGray};

    @media ${breakpoints.laptop} {
      display: flex;
      align-items: center;
    }
  `}
`

const TextBold = styled.div`
  ${body16BoldStyles}
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.dividerLineGray};
`

const PlusIcon = styled.button`
  z-index: 1;
  position: absolute;
  top: 0;
  right: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  opacity: 1;
  transform: translateX(16px);
  transition: opacity ${animation.quick} ${cubicBezier};
  width: 56px;
  height: 100%;
  padding: 0 16px;
  outline: none;
  border: none;
  background-color: transparent;

  &:focus,
  &:active {
    outline: none;
  }
`

const RemoveButton = styled.button`
  ${smallcapsRegular13Styles}
  align-items: center;
  justify-content: space-between;
  display: flex;
  height: 28px;
  padding: 0 4px 0 8px;
  outline: none;
  border: none;
  background-color: ${colors.backgroundGrayHover50};

  &:focus,
  &:active {
    outline: none;
  }

  &:hover {
    cursor: pointer;
  }
`

const CloseIcon = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 24px;
  height: 24px;
  margin-left: 4px;
  padding: 0 2px;
`

const InputWrapper = styled.div`
  position: relative;

  &:hover {
    ${PlusIcon} {
      svg {
        stroke: ${colors.black};
      }
    }
  }
`

const Input = styled.input`
  ${body14Styles}
  height: 56px;
  width: 100%;
  padding: 0 72px 0 0;
  border: none;
  font-size: 15px;
  font-weight: 400;
  color: ${colors.offBlack};

  &:focus,
  &:active {
    outline: none;
  }

  &:focus {
    ::-webkit-input-placeholder {
      visibility: hidden;
    }
    ::-moz-placeholder {
      visibility: hidden;
    }
    :-ms-input-placeholder {
      visibility: hidden;
    }
    :-moz-placeholder {
      visibility: hidden;
    }
  }

  &:hover {
    color: ${colors.black};

    ::-webkit-input-placeholder {
      color: ${colors.black};
    }
    ::-moz-placeholder {
      color: ${colors.black};
    }
    :-ms-input-placeholder {
      color: ${colors.black};
    }
    :-moz-placeholder {
      color: ${colors.black};
    }
  }

  ::-webkit-input-placeholder {
    color: ${colors.mediumGray};
  }
  ::-moz-placeholder {
    color: ${colors.mediumGray};
  }
  :-ms-input-placeholder {
    color: ${colors.mediumGray};
  }
  :-moz-placeholder {
    color: ${colors.mediumGray};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const CheckoutButtonWrapper = styled.div``

const PromoToastAnchor = styled.div`
  position: relative;
`

const MobilePromoToast = styled.div`
  ${smallcapsBold13Styles}
  align-items: center;
  justify-content: flex-start;
  display: flex;
  position: absolute;
  top: -64px;
  height: 48px;
  width: 100%;
  padding: 0 24px;
  background-color: ${colors.brandGreen};
  border-radius: 2px;
  border: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    display: none;
  }
`

const DesktopPromoToast = styled(MobilePromoToast)`
  display: none;

  @media ${breakpoints.laptop} {
    position: absolute;
    top: auto;
    bottom: calc(0.5 / 12 * 100vw);
    display: flex;
    width: calc(100% - 128px);
  }
`

const PromoText = styled.div`
  ${smallcapsBold13Styles}
  flex-shrink: 0;
  color: ${colors.offWhite};
  line-height: 22px;
`

const IconWrapper = styled.div`
  width: 24px;
  margin-right: 4px;
`

const Error = styled.div`
  ${smallcapsBold13Styles}
  color: ${colors.errorBackground};
`

const VerticalSpace = styled.div<{ $height?: string; $laptopHeight?: string }>`
  ${({ $height, $laptopHeight }) => css`
    height: ${$height || "20px"};

    @media ${breakpoints.laptop} {
      height: ${$laptopHeight || $height || "20px"};
    }
  `}
`

interface FooterProps {
  checkoutLink?: string
  discount?: string
  onAddDiscount: (discount: string | undefined) => DiscountResponseProps
  onAddDiscountSuccess: () => void
  onRemoveDiscount: () => void
  showPromoToast?: boolean
  subtotal: string
  total: string
}

const Footer: React.FC<FooterProps> = ({
  checkoutLink,
  discount,
  onAddDiscount,
  onAddDiscountSuccess,
  onRemoveDiscount,
  showPromoToast,
  subtotal,
  total,
}) => {
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  )
  const [discountCode, setDiscountCode] = React.useState<string>("")

  const handleAddDiscount = async () => {
    if (!discountCode) {
      return
    }

    const response: DiscountResponseProps = await onAddDiscount(discountCode)

    if (response && response.code === "DISCOUNT_NOT_FOUND") {
      setDiscountCode("")
      setErrorMessage("Oops, that's not a valid code")
    } else if (response && response.message) {
      setDiscountCode("")
      setErrorMessage(response.message)
    } else {
      onAddDiscountSuccess()
      setDiscountCode("")
      setErrorMessage(undefined)
    }
  }

  const onChange = ({ target: { value } }) => {
    setDiscountCode(value)
  }

  const onKeyDown = (event: any) => {
    if (event.key === ESCAPE_KEY) {
      event.currentTarget.blur()
      setDiscountCode("")
    }
    if (event.key === ENTER_KEY && event.currentTarget) {
      handleAddDiscount()
    }
  }

  return (
    <Container>
      <MobileAndTabletOnly>
        {discount && subtotal !== total && (
          <>
            <Row>
              <Text>Subtotal</Text>
              <Text>
                {formatCurrency(parseFloat(subtotal), "USD", "en-US")}
              </Text>
            </Row>
            <VerticalSpace $height="6px" />
            {/* CARTODO */}
            {/* <Row>
              <Text>Discount</Text>
              <Text>
                {formatCurrency(
                  parseFloat(subtotal) - parseFloat(total),
                  "USD",
                  "en-US"
                )}
              </Text>
            </Row>
            <VerticalSpace height="6px" /> */}
          </>
        )}
      </MobileAndTabletOnly>
      <DesktopOnly>
        <Header>Summary</Header>
        <VerticalSpace $laptopHeight="30px" />
        <Row>
          <Text>Subtotal</Text>
          <Text>{formatCurrency(parseFloat(subtotal), "USD", "en-US")}</Text>
        </Row>
        {/* {parseFloat(subtotal) !== parseFloat(total) && (
          <>
            <VerticalSpace $laptopHeight="12px" />
            <Row>
              <Text>Discount</Text>
              <Text $noWrap>
                {`- `}
                {formatCurrency(
                  parseFloat(subtotal) - parseFloat(total),
                  "USD",
                  "en-US"
                )}
              </Text>
            </Row>
          </>
        )} */}
        <VerticalSpace $laptopHeight="18px" />
        <Divider />
        <InputWrapper>
          <PlusIcon onClick={handleAddDiscount}>
            <Add strokeColor={colors.mediumGray} />
          </PlusIcon>
          <Input
            id="discountCodeInput"
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={"Discount Code"}
            type="text"
            value={discountCode}
          />
        </InputWrapper>
        {discount && (
          <>
            <RemoveButton onClick={onRemoveDiscount}>
              {discount}
              <CloseIcon>
                <Close strokeColor={colors.mediumGray} />
              </CloseIcon>
            </RemoveButton>
            <VerticalSpace $laptopHeight="16px" />
          </>
        )}
        {errorMessage && (
          <>
            <Error>{errorMessage}</Error>
            <VerticalSpace $laptopHeight="16px" />
          </>
        )}

        <Divider />
      </DesktopOnly>
      <VerticalSpace $height="0" $laptopHeight="60px" />
      <Row>
        <TextBold>Total</TextBold>
        <TextBold>{formatCurrency(parseFloat(total), "USD", "en-US")}</TextBold>
      </Row>
      <VerticalSpace $height="30px" $laptopHeight="18px" />
      <PromoToastAnchor>
        {showPromoToast && (
          <MobilePromoToast>
            <IconWrapper>
              <Check strokeColor={colors.offWhite} />
            </IconWrapper>
            <PromoText>Added to Cart</PromoText>
          </MobilePromoToast>
        )}
        <CheckoutButtonWrapper>
          <LinkButton
            external
            noDash
            text="Proceed to Checkout"
            to={checkoutLink || ""}
          />
        </CheckoutButtonWrapper>
      </PromoToastAnchor>
      {showPromoToast && (
        <DesktopPromoToast>
          <IconWrapper>
            <Check strokeColor={colors.offWhite} />
          </IconWrapper>
          <PromoText>Added to Cart</PromoText>
        </DesktopPromoToast>
      )}
    </Container>
  )
}

export default Footer
